import type { AxiosRequestConfig } from 'axios';

/**
 * The organisation a tenant-owned request is scoped to, as a total five-state value.
 *
 * Scope is not an optional argument that defaults to a meaning — it is a value that every
 * tenant-owned read and write must state. There is no sixth state, no absent case, and no default.
 *
 * The reason this type exists is that *absence used to have a meaning, and the meaning depended on
 * who was asking.* The API resolves a missing `X-Organisation-Id` two different ways: for a
 * GlobalAdmin it means every organisation; for every other role it means their own organisation,
 * taken from the `org_id` claim. Both behaviours are correct and both must survive. What could not
 * survive is one client-side value — `null` — standing for both, chosen by omission, and
 * disambiguated only at the far end of a network call.
 *
 * Each member is keyed by a DISTINCT literal property, and every member explicitly excludes the
 * other four via `?: never`.
 *
 * 🚨 Test membership by property presence — `in` at runtime, or an `!== undefined` check:
 *
 * ```ts
 * if ('organisationId' in scope) { ... }          // runtime branch: correct
 * if (scope.organisationId !== undefined) { ... } // same, and usable as a value guard
 * ```
 *
 * What you CANNOT do is rely on either to *narrow the type*. Because every member declares every
 * property — the others as `?: never` — the compiler sees `organisationId` as present on all five
 * constituents and will not discriminate the union. That is the price of non-widenability, and it
 * is a deliberate trade: a function returning a specific member must construct it explicitly rather
 * than returning the narrowed argument. See `toWriteScope` in `ResourceFormFields.tsx`.
 *
 * The `?: never` fields are load-bearing — do not delete them as boilerplate. Without them
 * TypeScript's excess-property check against a bare union admits any property belonging to ANY
 * member, so `{ allOrganisations: true, organisationId: 5 }` type-checks as a valid `OrgScope` and
 * `scopeHeaders` silently picks `organisationId` — a value that claims to be a platform-wide read
 * quietly becomes a single-organisation one. Spread-widening
 * (`{ ...ALL_ORGANISATIONS_SCOPE, organisationId: 5 }`) has the same effect. The exclusions are
 * what make "a member cannot be widened into another" true rather than merely intended.
 */
export type OrgScope =
	// a GlobalAdmin has not chosen yet — do not issue the request
	| {
			pending: true;
			organisationId?: never;
			ownOrganisation?: never;
			allOrganisations?: never;
			crossOrganisation?: never;
	  }
	// this one organisation
	| {
			organisationId: number;
			pending?: never;
			ownOrganisation?: never;
			allOrganisations?: never;
			crossOrganisation?: never;
	  }
	// the caller's own tenant, from their org_id claim
	| {
			ownOrganisation: true;
			pending?: never;
			organisationId?: never;
			allOrganisations?: never;
			crossOrganisation?: never;
	  }
	// deliberate, platform-wide READ
	| {
			allOrganisations: true;
			pending?: never;
			organisationId?: never;
			ownOrganisation?: never;
			crossOrganisation?: never;
	  }
	// deliberate, platform-wide WRITE — see CROSS_ORGANISATION_SCOPE
	| {
			crossOrganisation: true;
			pending?: never;
			organisationId?: never;
			ownOrganisation?: never;
			allOrganisations?: never;
	  };

/**
 * The scope a write may carry: one resolved organisation, the caller's own, or an explicit
 * cross-organisation bulk operation.
 *
 * A write cannot be issued before the organisation picker is satisfied (`pending` is excluded).
 * This matters more than it does for reads: the header derived from this value is what the API's
 * `TenantStampingSaveChangesInterceptor` uses to decide which tenant a new row lands in, and it
 * overwrites any `OrganisationId` present in the request body.
 *
 * 🚨 `allOrganisations` is excluded on purpose even though `crossOrganisation` is admitted and the
 * two produce an identical wire form. The distinction is not pedantry — it is the safety property:
 * `allOrganisations` is minted AUTOMATICALLY by `useListOrgScope` from a register list's filter,
 * so admitting it here would let a read scope drift into a write with nobody having decided that.
 * `crossOrganisation` cannot be produced by any hook; a call site must write
 * `CROSS_ORGANISATION_SCOPE` by hand. Deliberate stays deliberate, and stays greppable.
 *
 * Expressing the guard as a type means it holds even if a form's save-enabled expression is wrong.
 */
export type WriteScope = Extract<
	OrgScope,
	{ organisationId: number } | { ownOrganisation: true } | { crossOrganisation: true }
>;

/**
 * A tenant-owned read deliberately issued across every organisation.
 *
 * This constant is the *intended* way to construct the `allOrganisations` member, and together with
 * `useListOrgScope` it forms the audit surface: searching for these two identifiers is how the set
 * of deliberate platform-wide reads is enumerated.
 *
 * Be precise about the strength of that guarantee — it is a convention, not a compiler guarantee.
 * Nothing stops a call site writing `{ allOrganisations: true }` inline, and such a call site would
 * be invisible to the audit. Making it enforceable would require branding the member with a unique
 * symbol. Until that exists, a reviewer must read new `allOrganisations` usage rather than trusting
 * the grep to be complete.
 *
 * Not for globally-owned tables. `Roles`, `Organisations`, `RefreshTokens` and `EmailSettings` are
 * not tenant-owned at all, so their API functions take no scope parameter — using this constant for
 * them would bury the genuine platform-wide reads in rows that were never scoped to begin with.
 */
export const ALL_ORGANISATIONS_SCOPE = { allOrganisations: true } as const satisfies OrgScope;

/**
 * A bulk mutation a GlobalAdmin deliberately issues across organisation boundaries.
 *
 * 🚨 Only valid when `canChooseOrganisation` is true. The type cannot enforce that — check it at
 * the call site.
 *
 * Why this exists. The admin portal has 11 bulk endpoints (`deleteSites`, `deleteAreas`,
 * `deleteDocuments`, `bulkDeleteAssets`, `deleteWorkOrders`, `deleteUsers`, `deleteDepartments`,
 * `deleteResources`, `deleteRules`, `deleteMaintenanceTeams`, `bulkDeleteServicePartners`) and a
 * GlobalAdmin can select rows from two organisations in one register list. Sending a single
 * `X-Organisation-Id` for that selection does NOT fail loudly: the bulk handlers load through
 * `IRepository.Query()`, which applies the tenant query filter, so out-of-organisation rows are
 * silently NOT FOUND. The handler then deletes the subset it could see and returns success. The
 * operator asked to delete five things, five disappeared from the list, and two were never touched.
 *
 * Sending no header instead takes the server's `isUnscopedGlobalAdmin` path
 * (`TenantStampingSaveChangesInterceptor.cs:66`), which is the behaviour the portal relies on today
 * and is atomic across organisations.
 *
 * So the no-header write is not being introduced here — it already exists, as the thing you get by
 * default. What changes is that it must now be NAMED. Before this contract, forgetting an argument
 * produced it; now a call site has to say `CROSS_ORGANISATION_SCOPE` out loud, and every such site
 * can be found by searching for that identifier.
 */
export const CROSS_ORGANISATION_SCOPE = { crossOrganisation: true } as const satisfies OrgScope;

/**
 * Turns a scope into the axios config that carries it.
 *
 * Returns an `AxiosRequestConfig` so call sites can either spread it into a larger config or read
 * `.headers` off it for multipart uploads — the same two shapes the previous helper supported.
 *
 * Wire form:
 * - `{ organisationId: n }` → `X-Organisation-Id: n`
 * - `ownOrganisation` → no header; the server resolves the caller's `org_id` claim
 * - `allOrganisations` → no header; a GlobalAdmin has no claim, so the server reads platform-wide
 * - `crossOrganisation` → no header; takes the server's unscoped-GlobalAdmin write path
 * - `pending` → no header, but this should never happen (see below)
 *
 * `ownOrganisation` and `allOrganisations` intentionally share a wire form. They are distinct
 * TYPES because they are distinct intents, and the intent is what the call site is being forced to
 * declare. Collapsing them would restore the exact ambiguity this contract removes.
 *
 * The `pending` branch is NOT a fail-open default. Data hooks map a `pending` scope to
 * `enabled: false` and never issue the request, so this branch is unreachable in correct code; it
 * exists only so a leak degrades to the old behaviour rather than throwing inside a page render.
 * Do not add a runtime guard here — the enforcement belongs in the type system and at the hooks
 * layer, not in a check that costs latency on every request.
 */
export function scopeHeaders(scope: OrgScope): AxiosRequestConfig {
	return 'organisationId' in scope ? { headers: { 'X-Organisation-Id': String(scope.organisationId) } } : {};
}

/**
 * The write scope for a single-record mutation, or null while the organisation is unresolved.
 *
 * Null is what disables a save or an inline-create button — the absence of a valid `WriteScope`
 * rather than a separate readiness flag.
 *
 * 🚨 Rebuilds the member instead of returning the argument. Every `OrgScope` member declares every
 * property (the others as `?: never`, which is what stops one being widened into another), so the
 * compiler cannot discriminate the union by property presence. `in` and `!== undefined` still work
 * as runtime tests; they just cannot hand back a narrowed value.
 */
export function toWriteScope(scope: OrgScope): WriteScope | null {
	if (scope.organisationId !== undefined) return { organisationId: scope.organisationId };

	if (scope.ownOrganisation !== undefined) return { ownOrganisation: true };

	return null;
}

/**
 * Asserts that a write scope has resolved, and fails with something readable if it has not.
 *
 * `toWriteScope` returns null while the organisation is unresolved, and every mutation call site
 * has to turn that back into a value. The obvious way is `writeScope!`, and the obvious way is
 * wrong in a specific, unhelpful sense: a null then reaches `scopeHeaders`, where the first thing
 * that touches it is the `in` operator, so the user sees a save fail with
 * "Cannot use 'in' operator to search for 'organisationId' in null" — a message that says nothing
 * about organisations and points at the wrong file.
 *
 * The failure itself is correct behaviour and must stay: a write whose organisation is unknown has
 * to be refused, not sent with no header, because no header is a meaningful instruction to the
 * server (the unscoped-GlobalAdmin path) rather than an absence. This only makes the refusal say
 * what happened.
 *
 * Reaching it means a save control was enabled while the scope was still `pending`, which is a bug
 * in that page's enablement logic, not something a user can cause by clicking too early.
 */
export function requireWriteScope(scope: WriteScope | null): WriteScope {
	if (scope === null) {
		throw new Error(
			'Cannot write before the organisation is resolved. A save was issued while the scope was still pending — the control should have been disabled.',
		);
	}

	return scope;
}

/**
 * The write scope for a BULK mutation issued from a register list.
 *
 * A selection made on an all-organisations list can span organisations, and sending one
 * `X-Organisation-Id` for it does not fail loudly: the bulk handlers load through a tenant-filtered
 * query, so rows outside that organisation are simply not found, and the handler reports success
 * having deleted a subset. Mapping that case to `CROSS_ORGANISATION_SCOPE` sends no header and
 * takes the server's unscoped-GlobalAdmin path, which is atomic across organisations.
 *
 * Returns null only while the scope is `pending`, which should not occur on a rendered list.
 */
export function toBulkWriteScope(scope: OrgScope): WriteScope | null {
	if (scope.allOrganisations !== undefined) return CROSS_ORGANISATION_SCOPE;

	return toWriteScope(scope);
}
